import { Button, CircularProgress } from '@civilgrid/ui/components';
import type { ExtractAdminState } from '~/api/extract';
import { MajorHeading } from '../common/Heading';
import { templateAvailabilityLabel } from './common';
import ExtractAutomations from './ExtractAutomations';
import { EmptyRow, Table, tdClass } from './ExtractTable';

interface Props {
  state: ExtractAdminState | null;
  isLoading: boolean;
  /** True when the latest fetch failed; renders an explicit error/retry state instead of empty tables. */
  isError?: boolean;
  /** True while a (re)fetch is in flight; used to disable the refresh control. */
  isFetching?: boolean;
  onRefresh: () => void;
}

export default function ExtractState({ state, isLoading, isError = false, isFetching = false, onRefresh }: Props) {
  return (
    <div className="flex flex-col items-start space-y-6" data-testid="extract-state">
      <div className="flex items-center gap-4">
        <MajorHeading tooltip="Every automation and its status, active templates, and template→organization assignments.">
          Current Extract State
        </MajorHeading>
        <Button onClick={onRefresh} disabled={isLoading || isFetching} data-testid="extract-state-refresh-button">
          Refresh
        </Button>
      </div>

      {isLoading && <CircularProgress indeterminate />}

      {!isLoading && isError && (
        <div className="flex flex-col items-start gap-2" data-testid="extract-state-error">
          <p className="text-sm text-red-60">Failed to load extract state.</p>
          <Button onClick={onRefresh} disabled={isFetching} data-testid="extract-state-retry-button">
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && state && (
        <div className="flex w-full flex-col space-y-6">
          <ExtractAutomations automations={state.automations} />

          <section className="w-full">
            <div className="mb-1 text-sm font-medium">Template Availability ({state.activeTemplates.length})</div>
            <Table headers={['Template', 'Workspace', 'Available To']} testid="extract-state-availability">
              {state.activeTemplates.length ? (
                state.activeTemplates.map((template) => {
                  const orgNames = state.templateOrgAssignments
                    .filter((assignment) => assignment.extractTemplateId === template.id)
                    .map((assignment) => assignment.organizationName);
                  const availability = templateAvailabilityLabel(orgNames, state.organizationCount);
                  const isAll = availability === 'All organizations';
                  return (
                    <tr key={template.id}>
                      <td className={tdClass}>{template.name}</td>
                      <td className={tdClass}>
                        <code>{template.workspace}</code>
                      </td>
                      <td className={tdClass}>
                        {isAll ? (
                          <span className="font-medium text-blue-60">All organizations</span>
                        ) : (
                          <span className={orgNames.length ? '' : 'text-grey-60'}>{availability}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <EmptyRow colSpan={3} message="No active templates." />
              )}
            </Table>
          </section>
        </div>
      )}
    </div>
  );
}
